import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';
import { CronJobsConfig } from '../../../../config/cron-jobs.config';
import {
  Client,
  GuidDictionaryItem,
  SchedulerDataIn,
  SchedulerType,
} from '../../../../api/api-reference';
import { CronConverterComponent } from '../../../../libraries/cron-converter/cron-converter.component';

const cronEveryMinuteValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  if (!control.value) {
    return null;
  }

  return control.value === '* * * * *' ? { everyMinute: true } : null;
};

@Component({
  selector: 'app-scheduler-setup',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AngularSvgIconModule,
    RouterLink,
    ReactiveFormsModule,
    CronConverterComponent,
  ],
  templateUrl: './scheduler-setup.component.html',
  styleUrl: './scheduler-setup.component.scss',
})
export class SchedulerSetupComponent implements OnInit {
  edit = false;
  submitted = false;
  cronJobsConfig = CronJobsConfig;
  reminderId = '';
  readonly SchedulerType = SchedulerType;

  schedulerForm = this.fb.group(
    {
      name: ['', Validators.required],
      type: [null as SchedulerType | null, Validators.required],
      cron: ['', [Validators.required, cronEveryMinuteValidator]],
      regions: [[] as string[]],
      areas: [[] as string[]],
      assetTypes: [[] as string[]],
      meterMakes: [[] as string[]],
      meterModels: [[] as string[]],
    },
  );

  regions: GuidDictionaryItem[] = [];
  selectedRegions: GuidDictionaryItem[] = [];
  areas: GuidDictionaryItem[] = [];
  selectedAreas: GuidDictionaryItem[] = [];
  assetTypes: GuidDictionaryItem[] = [];
  selectedAssetTypes: GuidDictionaryItem[] = [];
  meterMakes: GuidDictionaryItem[] = [];
  selectedMeterMakes: GuidDictionaryItem[] = [];
  meterModels: GuidDictionaryItem[] = [];
  selectedMeterModels: GuidDictionaryItem[] = [];

  schedulerTypeDictionary: SchedulerType[] = Object.keys(SchedulerType)
    .filter((key) => isNaN(Number(key)))
    .map((key) => SchedulerType[key as keyof typeof SchedulerType])
    .filter((type) => type !== SchedulerType.FirmwareUpdate && type !== SchedulerType.MeterActivity);

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    public router: Router,
    public route: ActivatedRoute,
    private client: Client,
  ) { }

  get nameControl(): AbstractControl {
    return this.schedulerForm.get('name')!;
  }

  get typeControl(): AbstractControl {
    return this.schedulerForm.get('type')!;
  }

  get cronControl(): AbstractControl {
    return this.schedulerForm.get('cron')!;
  }

  get regionsControl(): AbstractControl {
    return this.schedulerForm.get('regions')!;
  }

  get areasControl(): AbstractControl {
    return this.schedulerForm.get('areas')!;
  }

  get assetTypesControl(): AbstractControl {
    return this.schedulerForm.get('assetTypes')!;
  }

  get meterMakesControl(): AbstractControl {
    return this.schedulerForm.get('meterMakes')!;
  }

  get meterModelsControl(): AbstractControl {
    return this.schedulerForm.get('meterModels')!;
  }

  get availableAreas(): GuidDictionaryItem[] {
    return this.areas.filter((item) => !this.isSelectedArea(item));
  }

  get availableRegions(): GuidDictionaryItem[] {
    return this.regions.filter((item) => !this.isSelectedRegion(item));
  }

  get availableAssetTypes(): GuidDictionaryItem[] {
    return this.assetTypes.filter((item) => !this.isSelectedAssetType(item));
  }

  get availableMeterMakes(): GuidDictionaryItem[] {
    return this.meterMakes.filter((item) => !this.isSelectedMeterMake(item));
  }

  get availableMeterModels(): GuidDictionaryItem[] {
    return this.meterModels.filter((item) => !this.isSelectedMeterModel(item));
  }

  get isPullSchedulerSelected(): boolean {
    return this.typeControl.value === SchedulerType.Pull;
  }

  ngOnInit(): void {
    const state = history.state;
    if (state && state.reminderData) {
      this.reminderId = state.reminderData;
    }

    if (this.reminderId) {
      this.getReminder();
    } else {
      this.getRegions();
      this.getAssetTypes();
      this.getMeterMakes();
    }
  }

  isControlInvalid(controlName: string): boolean {
    const control = this.schedulerForm.get(controlName);
    return !!control && control.invalid && (control.touched || this.submitted);
  }

  getAssetTypes(): void {
    this.client.getAllAssetTypesForOptions(undefined).subscribe({
      next: (response) => {
        this.assetTypes = response ?? [];
        this.selectedAssetTypes = this.assetTypes.filter((item) =>
          (this.assetTypesControl.value as string[] | null)?.includes(item.value!),
        );
      },
      error: (err) => {
        this.toastr.error(err.message);
      },
    });
  }

  getRegions(selectedAreaIds: string[] = []): void {
    this.client.getAllRegionsForOptions().subscribe({
      next: (response) => {
        this.regions = response ?? [];
        this.selectedRegions = this.regions.filter((item) =>
          (this.regionsControl.value as string[] | null)?.includes(item.value!),
        );

        if (this.selectedRegions.length > 0) {
          this.loadAreasForSelectedRegions(selectedAreaIds);
        }
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  getReminder(): void {
    this.client.getReminder(this.reminderId).subscribe({
      next: (response) => {
        this.edit = true;

        const selectedMeterMakeIds = this.getUniqueValues(
          response.meterModels?.map((item) => item.meterMakeId) ?? [],
        );
        const selectedMeterModelIds = this.getUniqueValues(
          response.meterModels?.map((item) => item.id) ?? [],
        );

        const selectedRegionIds = response.regions ?? [];

        this.schedulerForm.patchValue({
          name: response.name ?? '',
          type: response.type,
          cron: response.cron ?? '',
          regions: selectedRegionIds,
          areas: response.areas ?? [],
          assetTypes: response.assetTypes?.map(x => x.id!) ?? [],
          meterMakes: selectedMeterMakeIds,
          meterModels: selectedMeterModelIds,
        });

        this.getRegions(response.areas ?? []);
        this.getAssetTypes();
        this.getMeterMakes(selectedMeterModelIds);
        this.updatePullSelectionValidators(response.type);
        this.typeControl.disable({ emitEvent: false });
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  getMeterMakes(selectedMeterModelIds: string[] = []): void {
    this.client.getAllMeterMakersForSetupOptions().subscribe({
      next: (response) => {
        this.meterMakes = response ?? [];
        this.selectedMeterMakes = this.meterMakes.filter((item) =>
          (this.meterMakesControl.value as string[] | null)?.includes(item.value!),
        );
        this.loadMeterModelsForSelectedMakes(selectedMeterModelIds);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  private loadAreasForSelectedRegions(selectedAreaIds: string[] = []): void {
    const regionIds = this.selectedRegions
      .map((item) => item.value)
      .filter((value): value is string => !!value);

    if (regionIds.length === 0) {
      this.areas = [];
      this.clearSelectedAreas();
      return;
    }

    this.client.getAllAreaByRegionsForOptions(regionIds).subscribe({
      next: (response) => {
        this.areas = response ?? [];

        const idsToPreserve = selectedAreaIds.length > 0
          ? selectedAreaIds
          : this.selectedAreas
            .map((item) => item.value)
            .filter((value): value is string => !!value);

        this.selectedAreas = this.areas.filter(
          (item) => !!item.value && idsToPreserve.includes(item.value),
        );
        this.syncSelectedAreas();
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  isSelectedRegion(selectedValue: GuidDictionaryItem): boolean {
    return this.selectedRegions.some((item) => item.value === selectedValue.value);
  }

  onRegionChange(event: Event): void {
    const regionId = (event.target as HTMLSelectElement).value;
    if (!regionId) {
      return;
    }

    const selectedValue = this.regions.find((item) => item.value === regionId);

    if (selectedValue && !this.isSelectedRegion(selectedValue)) {
      this.selectedRegions.push(selectedValue);
      this.syncSelectedRegions();
      this.loadAreasForSelectedRegions();
    }

    (event.target as HTMLSelectElement).value = '';
  }

  removeRegion(selectedValue: GuidDictionaryItem): void {
    if (this.isSelectedRegion(selectedValue)) {
      this.selectedRegions = this.selectedRegions.filter(
        (item) => item.value !== selectedValue.value,
      );
      this.syncSelectedRegions();
      this.loadAreasForSelectedRegions();
    }
  }

  clearSelectedRegions(): void {
    this.selectedRegions = [];
    this.syncSelectedRegions();
    this.areas = [];
    this.clearSelectedAreas();
  }

  isSelectedArea(selectedValue: GuidDictionaryItem): boolean {
    return this.selectedAreas.some((item) => item.value === selectedValue.value);
  }

  onAreaChange(event: Event): void {
    const areaId = (event.target as HTMLSelectElement).value;
    if (!areaId) {
      return;
    }

    const selectedValue = this.areas.find((item) => item.value === areaId);
    if (selectedValue && !this.isSelectedArea(selectedValue)) {
      this.selectedAreas.push(selectedValue);
      this.syncSelectedAreas();
    }
  }

  removeArea(selectedValue: GuidDictionaryItem): void {
    if (this.isSelectedArea(selectedValue)) {
      this.selectedAreas = this.selectedAreas.filter(
        (item) => item.value !== selectedValue.value,
      );
      this.syncSelectedAreas();
    }
  }

  clearSelectedAreas(): void {
    this.selectedAreas = [];
    this.syncSelectedAreas();
  }

  isSelectedAssetType(selectedValue: GuidDictionaryItem): boolean {
    return this.selectedAssetTypes.some(
      (item) => item.value === selectedValue.value,
    );
  }

  onAssetTypeChange(event: Event): void {
    const assetTypeId = (event.target as HTMLSelectElement).value;
    if (!assetTypeId) {
      return;
    }

    const selectedValue = this.assetTypes.find(
      (item) => item.value === assetTypeId,
    );
    if (selectedValue && !this.isSelectedAssetType(selectedValue)) {
      this.selectedAssetTypes.push(selectedValue);
      this.syncSelectedAssetTypes();
    }
  }

  removeAssetType(selectedValue: GuidDictionaryItem): void {
    if (this.isSelectedAssetType(selectedValue)) {
      this.selectedAssetTypes = this.selectedAssetTypes.filter(
        (item) => item.value !== selectedValue.value,
      );
      this.syncSelectedAssetTypes();
    }
  }

  clearSelectedAssetTypes(): void {
    this.selectedAssetTypes = [];
    this.syncSelectedAssetTypes();
  }

  isSelectedMeterMake(selectedValue: GuidDictionaryItem): boolean {
    return this.selectedMeterMakes.some((item) => item.value === selectedValue.value);
  }

  onMeterMakeChange(event: Event): void {
    const meterMakeId = (event.target as HTMLSelectElement).value;
    if (!meterMakeId) {
      return;
    }

    const selectedValue = this.meterMakes.find((item) => item.value === meterMakeId);
    if (selectedValue && !this.isSelectedMeterMake(selectedValue)) {
      this.selectedMeterMakes.push(selectedValue);
      this.syncSelectedMeterMakes();
      this.loadMeterModelsForSelectedMakes();
    }
  }

  removeMeterMake(selectedValue: GuidDictionaryItem): void {
    if (this.isSelectedMeterMake(selectedValue)) {
      this.selectedMeterMakes = this.selectedMeterMakes.filter(
        (item) => item.value !== selectedValue.value,
      );
      this.syncSelectedMeterMakes();
      this.loadMeterModelsForSelectedMakes();
    }
  }

  clearSelectedMeterMakes(): void {
    this.selectedMeterMakes = [];
    this.syncSelectedMeterMakes();
    this.meterModels = [];
    this.clearSelectedMeterModels();
  }

  isSelectedMeterModel(selectedValue: GuidDictionaryItem): boolean {
    return this.selectedMeterModels.some(
      (item) => item.value === selectedValue.value,
    );
  }

  onMeterModelChange(event: Event): void {
    const meterModelId = (event.target as HTMLSelectElement).value;
    if (!meterModelId) {
      return;
    }

    const selectedValue = this.meterModels.find(
      (item) => item.value === meterModelId,
    );
    if (selectedValue && !this.isSelectedMeterModel(selectedValue)) {
      this.selectedMeterModels.push(selectedValue);
      this.syncSelectedMeterModels();
    }
  }

  removeMeterModel(selectedValue: GuidDictionaryItem): void {
    if (this.isSelectedMeterModel(selectedValue)) {
      this.selectedMeterModels = this.selectedMeterModels.filter(
        (item) => item.value !== selectedValue.value,
      );
      this.syncSelectedMeterModels();
    }
  }

  clearSelectedMeterModels(): void {
    this.selectedMeterModels = [];
    this.syncSelectedMeterModels();
  }

  onSchedulerTypeChange(type: SchedulerType): void {
    if (this.edit) {
      return;
    }

    this.typeControl.setValue(type);
    this.updatePullSelectionValidators(type);

    if (type !== SchedulerType.Pull) {
      this.clearSelectedMeterMakes();
    }
  }

  private loadMeterModelsForSelectedMakes(
    selectedMeterModelIds: string[] = [],
  ): void {

    const selectedMeterMakeIds = this.selectedMeterMakes
      .map((item) => item.value)
      .filter((value): value is string => !!value);

    if (selectedMeterMakeIds.length === 0) {
      this.meterModels = [];
      this.clearSelectedMeterModels();
      return;
    }

    this.client.getAllMeterModelForMeterMakes(selectedMeterMakeIds).subscribe({
      next: (response) => {
        this.meterModels = response ?? [];

        const idsToPreserve = selectedMeterModelIds.length > 0
          ? selectedMeterModelIds
          : this.selectedMeterModels
            .map((item) => item.value)
            .filter((value): value is string => !!value);

        this.selectedMeterModels = this.meterModels.filter(
          (item) => !!item.value && idsToPreserve.includes(item.value),
        );
        this.syncSelectedMeterModels();
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  private syncSelectedRegions(): void {
    this.regionsControl.setValue(this.selectedRegions.map((item) => item.value!));
    this.regionsControl.markAsDirty();
  }

  private syncSelectedAreas(): void {
    this.areasControl.setValue(this.selectedAreas.map((item) => item.value!));
    this.areasControl.markAsDirty();
  }

  private syncSelectedAssetTypes(): void {
    this.assetTypesControl.setValue(
      this.selectedAssetTypes.map((item) => item.value!),
    );
    this.assetTypesControl.markAsDirty();
  }

  private syncSelectedMeterMakes(): void {
    this.meterMakesControl.setValue(
      this.selectedMeterMakes.map((item) => item.value!),
    );
    this.meterMakesControl.markAsDirty();
  }

  private syncSelectedMeterModels(): void {
    this.meterModelsControl.setValue(
      this.selectedMeterModels.map((item) => item.value!),
    );
    this.meterModelsControl.markAsDirty();
  }

  private getUniqueValues(values: Array<string | null | undefined>): string[] {
    return [...new Set(values.filter((value): value is string => !!value))];
  }

  private updatePullSelectionValidators(type: SchedulerType | null): void {
    if (type === SchedulerType.Pull) {
      this.meterMakesControl.setValidators(Validators.required);
      this.meterModelsControl.setValidators(Validators.required);
    } else {
      this.meterMakesControl.clearValidators();
      this.meterModelsControl.clearValidators();
    }

    this.meterMakesControl.updateValueAndValidity({ emitEvent: false });
    this.meterModelsControl.updateValueAndValidity({ emitEvent: false });
  }

  onSubmit(): void {
    this.submitted = true;

    if (this.schedulerForm.invalid) {
      this.schedulerForm.markAllAsTouched();
      return;
    }

    const formValue = this.schedulerForm.getRawValue();
    const sanitizedCron = (formValue.cron ?? '')
      .trim()
      .split(' ')
      .map((part) => (part === 'NaN' ? '0' : part))
      .join(' ');

    const reminderDataIn = Object.assign(new SchedulerDataIn(), {
      id: this.reminderId || undefined,
      name: formValue.name,
      cron: sanitizedCron,
      regions: formValue.regions ?? [],
      assetTypes: formValue.assetTypes ?? [],
      areas: formValue.areas ?? [],
      type: formValue.type,
      meterModels:
        formValue.type === SchedulerType.Pull ? formValue.meterModels ?? [] : [],
    });

    this.client.saveScheduler(reminderDataIn).subscribe({
      next: () => {
        this.edit = false;
        this.toastr.success('Successfully saved.');
        this.router.navigate(['/settings/scheduler/schedules']);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
}
